import { useTranslation } from 'react-i18next';
import type { CustomerSummary } from '@optiflow/types';
import { Card, CardHeader } from '@/components/ui/Card';
import { useLocale } from '@/hooks/useLocale';
import { formatDate } from '@/utils/format';

interface DetailProps {
  label: string;
  value: string | null;
  /** Numbers, emails and dates read left-to-right even inside an Arabic layout. */
  ltr?: boolean;
}

function Detail({ label, value, ltr = false }: DetailProps) {
  return (
    <div className="min-w-0">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink mt-0.5 truncate text-sm" {...(ltr ? { dir: 'ltr' as const } : {})}>
        {value && value.trim() ? value : '—'}
      </dd>
    </div>
  );
}

/** Basic and contact information for the profile page. */
export function CustomerSummaryCard({ customer }: { customer: CustomerSummary }) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader title={t('customers.sections.basic')} />
        <dl className="grid grid-cols-2 gap-4 p-5">
          <Detail
            label={t('customers.fields.customerNumber')}
            value={customer.customerNumber}
            ltr
          />
          <Detail label={t('customers.fields.fullName')} value={customer.displayName} />
          <Detail
            label={t('customers.fields.dateOfBirth')}
            value={customer.dateOfBirth ? formatDate(customer.dateOfBirth, locale) : null}
            ltr
          />
          <Detail
            label={t('customers.fields.gender')}
            value={
              customer.gender === 'UNSPECIFIED'
                ? null
                : t(`customers.gender.${customer.gender.toLowerCase()}`)
            }
          />
          <Detail
            label={t('customers.fields.preferredLanguage')}
            value={t(`customers.language.${customer.preferredLanguage.toLowerCase()}`)}
          />
          <Detail
            label={t('customers.fields.registered')}
            value={formatDate(customer.createdAt, locale)}
          />
        </dl>
      </Card>

      <Card>
        <CardHeader title={t('customers.sections.contact')} />
        <dl className="grid grid-cols-2 gap-4 p-5">
          <Detail label={t('customers.fields.mobile')} value={customer.mobile} ltr />
          <Detail
            label={t('customers.fields.alternateMobile')}
            value={customer.alternateMobile}
            ltr
          />
          <Detail label={t('customers.fields.email')} value={customer.email} ltr />
          <Detail label={t('customers.fields.notes')} value={customer.notes} />
        </dl>
      </Card>
    </div>
  );
}
